// @flow
import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { CartesianGrid, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList } from 'recharts';
import { translate } from 'react-i18next';
import WarningIcon from 'material-ui/svg-icons/alert/warning';

import LoadingSpinner from 'common/components/LoadingSpinner';
import { displayPercent } from 'common/components/Number';
import CommercialAgeGroup from 'common/components/charts/CommercialAgeGroup';
import AgeRangeReaches from 'common/domain/AgeRangeReaches';
import AgeRangeGraphTooltip from './tooltip/AgeRangeGraphTooltip';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const GraphWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection};
`;

const AgeRangeContainer = styled.div`
  width: 100%;
`;

const ErrorMessageWrapper = styled.span`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

type DataBar = {
  key: string;
  color: string;
}

type AgeRangeGraphProps = {
  data?: AgeRangeReaches;
  height?: number | string;
  width?: number | string;
  errorStyle?: Object;

  isLoading?: boolean;
  hideCommercialAgeGroup?: boolean;
  commercialAgeGroupPlacement: "right" | "bottom";
  animate?: boolean;
  showLabels?: boolean;
  showMissingDataLegend?: boolean;
  hideInterpolated?: boolean;

  theme: Object;
  t: any;
};

class AgeRangeGraph extends React.Component<AgeRangeGraphProps> {

  ageRangeDataToArray = (ranges: AgeRangeReaches) => {
    const { hideInterpolated } = this.props;
    const ageRangeReaches = ranges.asPercent();
    const reaches = ageRangeReaches.map(x => x.reach);
    const maxValue = Math.max(...reaches) * 100;

    return ageRangeReaches.reduce((acc, ageRangeReach) => {
      const value = {};
      value.ageRange = ageRangeReach.range;

      if (ageRangeReach.isInterpolated() && !hideInterpolated) {
        value.interpolated = ageRangeReach.reach * 100;
      } else {
        value.percentage = ageRangeReach.reach * 100;
      }

      value.noData = ageRangeReach.reach === 0 ? maxValue : 0;
      acc.push(value);
      return acc;
    }, []);
  };

  renderCustomLabel = graphData => ({ x, y, stroke, width, height, index }) => {
    if (!graphData[index].noData) {
      return null;
    }

    const newX = x + (width / 2);
    const newY = y + (height / 2);
    return <text className="material-icons" x={newX} y={newY} fill={'#BFBFBF'} fontSize={10} textAnchor="middle">warning</text>;
  };

  labelsFormatter(label) {
    if (label === 0) {
      return null;
    }

    return displayPercent(label / 100);
  }

  renderAgeRangeGraph = (data: AgeRangeReaches) => {
    const { height, width } = this.props;
    const { colors } = this.props.theme;
    const graphData = this.ageRangeDataToArray(data);

    const dataBars: DataBar[] = [
      { key: 'percentage', color: colors.primary },
      { key: 'interpolated', color: colors.warning },
    ];

    return (
      <AgeRangeContainer style={{ height: height || 175 }}>
        <ResponsiveContainer height={height} width={width || '99%'}>
          <BarChart data={graphData} label >
            <CartesianGrid stroke="#ddd" strokeDasharray="5 5" vertical={false} />
            <XAxis dataKey="ageRange" stroke={colors.primaryText} />
            <YAxis width={25} />
            <Tooltip content={<AgeRangeGraphTooltip />} />
            <Tooltip
              formatter={value => (value === 0 ? null : displayPercent(value / 100))}
            />
            { dataBars.map(bar => this.renderBar(bar))}
            <Bar
              animationEasing="ease-in-out"
              barSize={100}
              dataKey={'noData'}
              stackId="total"
              fill={'#EFEFEF'}
              label={this.renderCustomLabel(graphData)}
              isAnimationActive={false} // this fixed a bug with label not showing on first render
            />
          </BarChart>
        </ResponsiveContainer>
      </AgeRangeContainer>
    );
  }

  renderBar(bar: DataBar) {
    const { showLabels, animate } = this.props;
    const { colors } = this.props.theme;

    return (
      <Bar
        animationEasing="ease-in-out"
        barSize={100}
        dataKey={bar.key}
        stackId="total"
        fill={bar.color}
        isAnimationActive={animate}
        key={bar.key}
      >
        {showLabels &&
          <LabelList formatter={this.labelsFormatter} position="insideTop" fill={colors.tertiaryText} fontSize="10px" key={bar.key} />
        }
      </Bar>
    );
  }

  render() {
    const { data, hideCommercialAgeGroup, errorStyle, t, isLoading, showMissingDataLegend } = this.props;
    const { fonts } = this.props.theme;

    const ageGroupPlacement = this.props.commercialAgeGroupPlacement || 'right';
    const flexDirection = ageGroupPlacement === 'right' ? 'row' : 'column';

    return (
      <Wrapper>
        {isLoading &&
          <LoadingSpinner />
        }
        {!data && !isLoading &&
          <span style={{ ...fonts.style.cardTitle, ...errorStyle }}>
            {t('noDataAvailable')}
          </span>
        }
        {data && !isLoading &&
          <ContentWrapper>
            <MainWrapper flexDirection={flexDirection}>
              <GraphWrapper>
                {this.renderAgeRangeGraph(data)}
              </GraphWrapper>
              {!hideCommercialAgeGroup &&
                <CommercialAgeGroup ageGroup={data} direction={ageGroupPlacement === 'right' ? 'vertical' : 'horizontal'} />
              }
            </MainWrapper>
            {showMissingDataLegend && data.hasInsufficientData() &&
              <ErrorMessageWrapper><WarningIcon color={'#BFBFBF'} /> : {t('insufficientData')}</ErrorMessageWrapper>
            }
          </ContentWrapper>
        }
      </Wrapper>
    );
  }
}

export default withTheme(translate()(AgeRangeGraph));



// WEBPACK FOOTER //
// ./src/common/components/charts/AgeRangeGraph.js